import { DefaultService } from 'gen/stream-api/requests/services.gen';
import type { SettlementStyleResponse } from 'gen/stream-api/requests/types.gen';
import { dayjs } from 'util/shared/date';

export type ExerciseStyle = 'european' | 'american';
export type ExerciseStyleName = ExerciseStyle;
export type ExerciseStylesRecord = Record<string, ExerciseStyle>;

export const DEFAULT_EXERCISE_STYLE: ExerciseStyleName = 'american';

export const UNIFORM_EXERCISE_STYLE_KEY = '*';

export function parseExerciseStyles(
  body: SettlementStyleResponse | null,
): ExerciseStylesRecord | null {
  if (body == null) {
    return null;
  }
  if (body.styles != null && Object.keys(body.styles).length > 0) {
    return body.styles;
  }
  if (body.uniform_style != null) {
    return { [UNIFORM_EXERCISE_STYLE_KEY]: body.uniform_style };
  }
  return null;
}

/**
 * Fetch per-expiry exercise styles (European/American) for one symbol from the
 * stream settlement_style endpoint. Returns null on any failure so callers can
 * fall back to the engine's American default.
 */
export async function fetchExerciseStyles(
  symbol: string,
): Promise<ExerciseStylesRecord | null> {
  try {
    const body = await DefaultService.getV1SettlementStyle({ symbol });
    return parseExerciseStyles(body);
  } catch (_err) {
    return null;
  }
}

export function resolveStyleForExpiryDate(
  styles: ExerciseStylesRecord | null | undefined,
  expiryDate: string,
): ExerciseStyleName {
  if (styles != null) {
    const exact = styles[expiryDate];
    if (exact === 'european' || exact === 'american') {
      return exact;
    }
    const uniform = styles[UNIFORM_EXERCISE_STYLE_KEY];
    if (uniform === 'european' || uniform === 'american') {
      return uniform;
    }
  }
  return DEFAULT_EXERCISE_STYLE;
}

export function resolveStyleForExpiry(
  styles: ExerciseStylesRecord | null | undefined,
  expiryEpochMs: number,
): ExerciseStyleName {
  if (styles == null || !Number.isFinite(expiryEpochMs)) {
    return DEFAULT_EXERCISE_STYLE;
  }
  return resolveStyleForExpiryDate(
    styles,
    dayjs.utc(expiryEpochMs).format('YYYY-MM-DD'),
  );
}
